// Canonical sharing-UI template — API client factory.
// createSharingClient({ apiBase, getToken }) returns a fully-typed client for the
// StructuraMatrix.SharingModule HTTP contract. Authed calls attach the bearer
// token; the anonymous link fetch deliberately does not.

import {
  SharingApiError,
  type SharingClient,
  type SharingConfig,
  type ShareGrant,
  type ShareLinkInfo,
  type SharedViewResponse,
  type SharedWithMeItem,
  type LinkViewResponse,
} from "./types";

async function parseOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new SharingApiError(res.status, body?.error ?? `Forespørselen feilet (HTTP ${res.status}).`);
  }
  return (await res.json()) as T;
}

export function createSharingClient(config: SharingConfig): SharingClient {
  const authFetch = (url: string, init?: RequestInit): Promise<Response> => {
    const token = config.getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(init?.headers as Record<string, string> | undefined),
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(url, { ...init, headers });
  };

  const mgmt = (resourceType: string, resourceId: string): string =>
    `${config.apiBase}/sharing/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}`;

  return {
    async listShares(resourceType, resourceId): Promise<ShareGrant[]> {
      const res = await authFetch(`${mgmt(resourceType, resourceId)}/shares`, { cache: "no-store" });
      return (await parseOrThrow<{ shares: ShareGrant[] }>(res)).shares;
    },
    async addShare(resourceType, resourceId, granteeEmail): Promise<ShareGrant> {
      const res = await authFetch(`${mgmt(resourceType, resourceId)}/shares`, {
        method: "POST",
        body: JSON.stringify({ granteeEmail }),
      });
      return (await parseOrThrow<{ share: ShareGrant }>(res)).share;
    },
    async revokeShare(resourceType, resourceId, granteeUserId): Promise<void> {
      const res = await authFetch(`${mgmt(resourceType, resourceId)}/shares/${encodeURIComponent(granteeUserId)}`, { method: "DELETE" });
      if (!res.ok) await parseOrThrow<void>(res);
    },
    async listLinks(resourceType, resourceId): Promise<ShareLinkInfo[]> {
      const res = await authFetch(`${mgmt(resourceType, resourceId)}/links`, { cache: "no-store" });
      return (await parseOrThrow<{ links: ShareLinkInfo[] }>(res)).links;
    },
    async createLink(resourceType, resourceId, expiryDays, label): Promise<ShareLinkInfo> {
      const res = await authFetch(`${mgmt(resourceType, resourceId)}/links`, {
        method: "POST",
        body: JSON.stringify({ expiryDays, label }),
      });
      return (await parseOrThrow<{ link: ShareLinkInfo }>(res)).link;
    },
    async revokeLink(resourceType, resourceId, token): Promise<void> {
      const res = await authFetch(`${mgmt(resourceType, resourceId)}/links/${encodeURIComponent(token)}`, { method: "DELETE" });
      if (!res.ok) await parseOrThrow<void>(res);
    },
    async listSharedWithMe(): Promise<SharedWithMeItem[]> {
      const res = await authFetch(`${config.apiBase}/shared`, { cache: "no-store" });
      return (await parseOrThrow<{ items: SharedWithMeItem[] }>(res)).items;
    },
    async getShared(shareId): Promise<SharedViewResponse> {
      const res = await authFetch(`${config.apiBase}/shared/${encodeURIComponent(shareId)}`, { cache: "no-store" });
      return await parseOrThrow<SharedViewResponse>(res);
    },
    async getLink(token): Promise<LinkViewResponse> {
      // Anonymous — the token IS the credential; deliberately not authFetch.
      const res = await fetch(`${config.apiBase}/link/${encodeURIComponent(token)}`, { cache: "no-store" });
      return await parseOrThrow<LinkViewResponse>(res);
    },
  };
}
